import { Box, Typography, Grid } from "@mui/material";
import OrderDetailsHeader from "./sections/OrderDetailsHeader";
import OrderSummary from "./sections/OrderSummary";
import OrderImages from "./sections/OrderImages";
import ProductDetails from "./sections/ProductDetails";
import AttachedDocuments from "./sections/AttachedDocuments";
import DeliverySchedule from "./sections/DeliverySchedule";
import RelatedOrders from "./sections/RelatedOrders";
import OrderDetailsCard from "./sections/OrderDetails";
import NeedsDeliveryScheduled from "./sections/NeedsDeliveryScheduled";
import Notes from "./sections/Notes";
import { OrderDetailsResponse } from "@/app/interfaces/response/OrderDetailsResponse";
import OrderStatusTimeline from "./sections/OrderStatusTimeline";
import { DQDeliveryOrderResponse } from "@/app/interfaces/response/DQDeliveryOrderResponse";
import Ghost from "../shared/ghost";
import {
  ORDER_DETAILS_IMAGES_GHOST,
  ORDER_STATUS_TIMELINE_GHOST,
} from "@/app/constants/ghostHeights";
import GhostList from "../shared/ghostList";
import OrderDetailsSectionsLayoutGhost from "./OrderDetailsSectionsLayoutGhost";
import RelatedQuotes from "./sections/RelatedQuotes";

const OrderDetailsSectionsLayout = ({
  isOrderLoading,
  order,
  isDQLoading,
  dqData,
  isSAPOrder,
}: {
  order: OrderDetailsResponse | undefined;
  isDQLoading: boolean;
  dqData: DQDeliveryOrderResponse | undefined;
  isOrderLoading: boolean;
  isSAPOrder: boolean;
}) => {
  if (isOrderLoading || !order) {
    return <OrderDetailsSectionsLayoutGhost />;
  }

  const imageUrls = dqData?.images?.map((img) => img.picturePath) || [];
  const signatureUrl = dqData?.order?.signatures?.[0]?.signature
    ? `data:image/png;base64,${dqData.order.signatures[0].signature}`
    : null;

  const allDQDatesNull = !(
    dqData?.order?.orderDate ||
    dqData?.order?.tripTimeOut ||
    dqData?.order?.arrivalTime ||
    dqData?.order?.departureTime
  );

  const SPACING_UNIT = 1;

  return (
    <>
      <Box className="page-title" mb={0}>
        <Typography
          variant="h1"
          textAlign="center"
          sx={{
            fontSize: { xs: "1.8rem !important", sm: "2.2rem !important" },
          }}
        >
          Order # {order.customOrderNumber}
        </Typography>
      </Box>
      <Box
        sx={{
          backgroundColor: "#f8f8f8",
          p: 3,
          display: "flex",
          flexDirection: "column",
          gap: SPACING_UNIT,
        }}
      >
        <Box>
          <OrderDetailsHeader
            orderId={order.id}
            shouldShowAttachments={order.attachedDocuments.length > 0}
          />
        </Box>
        <Box>
          {isDQLoading ? (
            <Ghost height={ORDER_STATUS_TIMELINE_GHOST} />
          ) : (
            !allDQDatesNull && (
              <OrderStatusTimeline
                dates={{
                  created: dqData?.order?.orderDate ?? null,
                  enRoute: dqData?.order?.tripTimeOut ?? null,
                  arrived: dqData?.order?.arrivalTime ?? null,
                  delivered: dqData?.order?.departureTime ?? null,
                }}
              />
            )
          )}
        </Box>
        <Grid container spacing={SPACING_UNIT}>
          <Grid
            item
            xs={12}
            md={5.5}
            sx={{ display: "flex", flexDirection: "column", gap: SPACING_UNIT }}
          >
            <OrderDetailsCard order={order} />
            {order.orderDetailsActions.showDeliveryScheduleSection && (
              <NeedsDeliveryScheduled
                orderId={order.id}
                holidays={order.publicHolidays}
                scheduledDate={
                  order.selectedDeliveryDate ||
                  order.promisedDate ||
                  order.pickedDate
                }
                deliveryTime={order.selectedDeliveryTime}
                needsDeliveryScheduled={
                  order.orderDetailsActions.needsDeliveryScheduled
                }
              />
            )}
          </Grid>
          <Grid item xs={12} md={3}>
            <OrderSummary order={order} />
          </Grid>
          <Grid
            item
            xs={12}
            md={3.5}
            sx={{ display: "flex", flexDirection: "column", gap: SPACING_UNIT }}
          >
            {isDQLoading ? (
              <GhostList ghostHeight={ORDER_DETAILS_IMAGES_GHOST} count={3} />
            ) : (
              <>
                {!order.pickUpInStore && (
                  <OrderImages images={imageUrls} signatureUrl={signatureUrl} />
                )}
                <AttachedDocuments
                  attachments={order.attachedDocuments}
                  eagleViewDocuments={order.eagleViewDocuments}
                  orderId={order.id}
                />
                {dqData?.delivery?.deliveryDate &&
                  dqData?.delivery?.isDelivered && (
                    <DeliverySchedule deliveryData={dqData?.delivery} />
                  )}
              </>
            )}
          </Grid>
        </Grid>
        <Box
          sx={{ display: "flex", flexDirection: "column", gap: SPACING_UNIT }}
        >
          {order.items.length > 0 && (
            <ProductDetails
              isSAPOrder={isSAPOrder}
              productItems={order.items}
              orderId={order.id}
            />
          )}
          {order.orderNotes.length > 0 && <Notes notes={order.orderNotes} />}
          {order.relatedOrders.length > 0 && (
            <RelatedOrders relatedOrders={order.relatedOrders} />
          )}
          {order.relatedQuotes.length > 0 && (
            <RelatedQuotes relatedQuotes={order.relatedQuotes} />
          )}
        </Box>
      </Box>
    </>
  );
};

export default OrderDetailsSectionsLayout;
